<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useGenerationTaskStore } from '@/Stores/generationTaskStore';
import { useUserStore } from '@/Stores/userStore';
import { useInterfaceStore } from '@/Stores/interfaceStore';

const generationStore = useGenerationTaskStore();
const userStore = useUserStore();
const interfaceStore = useInterfaceStore();

const isExpanded = ref(false);
const elapsedTimers = ref({});
let timerInterval = null;

// Dragging state
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });
const dragStart = ref({ x: 0, y: 0 });
const hasDragged = ref(false);
const DRAG_THRESHOLD = 5;
const indicatorRef = ref(null);

const positionStyle = computed(() => {
    const pos = interfaceStore.generationIndicatorPosition;
    if (pos) {
        return { left: pos.x + 'px', top: pos.y + 'px' };
    }
    return { left: '1rem', top: '4rem' };
});

function onMouseDown(e) {
    if (e.button !== 0) return;
    const el = indicatorRef.value;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragOffset.value = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragStart.value = { x: e.clientX, y: e.clientY };
    hasDragged.value = false;
    isDragging.value = false;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
}

function onMouseMove(e) {
    const dx = e.clientX - dragStart.value.x;
    const dy = e.clientY - dragStart.value.y;

    if (!hasDragged.value && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;

    hasDragged.value = true;
    isDragging.value = true;

    const x = e.clientX - dragOffset.value.x;
    const y = e.clientY - dragOffset.value.y;

    interfaceStore.setGenerationIndicatorPosition({ x, y });
}

function onMouseUp() {
    isDragging.value = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
}

function onPillClick() {
    if (!hasDragged.value) {
        isExpanded.value = !isExpanded.value;
    }
}

const typeIcons = {
    character: ['fas', 'dungeon'],
    creature: ['far', 'paw-claws'],
    npc: ['fas', 'user'],
    equipment: ['far', 'sword'],
    illustration: ['fas', 'paintbrush'],
};

const typeLabels = {
    character: 'Character',
    creature: 'Creature',
    npc: 'NPC',
    equipment: 'Equipment',
    illustration: 'Illustration',
};

function getTypeIcon(type) {
    return typeIcons[type] || ['fas', 'wand-magic-sparkles'];
}

function getTypeLabel(type) {
    return typeLabels[type] || 'Generation';
}

function getElapsedTime(startedAt) {
    if (!startedAt) return '0s';
    const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    if (elapsed < 60) return `${elapsed}s`;
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}m ${seconds}s`;
}

function getInfluenceText(task) {
    if (!task.metadata) return '';
    return task.metadata.influence || '';
}

// Tick elapsed times
function startTimer() {
    if (timerInterval) return;
    timerInterval = setInterval(() => {
        // Force re-render by updating a reactive counter
        elapsedTimers.value = { ...elapsedTimers.value, tick: Date.now() };
    }, 1000);
}

function stopTimer() {
    if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
    }
}

const isVisible = computed(() => {
    return generationStore.activeCount > 0;
});

watch(isVisible, (val) => {
    if (val) {
        startTimer();
    } else {
        stopTimer();
        isExpanded.value = false;
    }
});

// Initialize store when user is logged in
watch(() => userStore.userId, (userId) => {
    if (userId) {
        generationStore.initialize(userId);
    }
}, { immediate: true });

onMounted(() => {
    if (isVisible.value) {
        startTimer();
    }
});

onUnmounted(() => {
    stopTimer();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
});
</script>

<template>
    <Transition name="indicator-slide">
        <div v-if="isVisible" ref="indicatorRef" class="fixed z-50" :style="positionStyle" :class="{ 'select-none': isDragging }">
            <!-- Collapsed pill -->
            <div
                class="flex items-center gap-2 bg-gray-800 border border-gray-600 rounded-full px-3 py-1.5 shadow-lg transition-colors whitespace-nowrap"
                :class="isDragging ? 'cursor-grabbing' : 'cursor-grab hover:bg-gray-700'"
                @mousedown="onMouseDown"
                @click="onPillClick"
            >
                <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-amber-400 animate-spin" size="sm" />
                <span class="text-sm text-gray-200 font-medium">{{ generationStore.activeCount }} generating</span>
                <FontAwesomeIcon
                    :icon="['fas', 'chevron-down']"
                    class="text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': isExpanded }"
                    size="xs"
                />
            </div>

            <!-- Expanded task list -->
            <Transition name="expand">
                <div v-if="isExpanded" class="mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg overflow-hidden min-w-64 max-w-80">
                    <div
                        v-for="task in generationStore.activeTasks"
                        :key="task.id"
                        class="flex items-center gap-3 px-3 py-2 border-b border-gray-700 last:border-b-0"
                    >
                        <FontAwesomeIcon :icon="getTypeIcon(task.type)" class="text-amber-400" size="sm" />
                        <div class="flex-1 min-w-0">
                            <p class="text-sm text-gray-200 font-medium">{{ getTypeLabel(task.type) }}</p>
                            <p v-if="getInfluenceText(task)" class="text-xs text-gray-400 truncate">{{ getInfluenceText(task) }}</p>
                        </div>
                        <span class="text-xs text-gray-500 whitespace-nowrap">{{ getElapsedTime(task.started_at) }}</span>
                    </div>
                </div>
            </Transition>
        </div>
    </Transition>
</template>

<style scoped>
.indicator-slide-enter-active,
.indicator-slide-leave-active {
    transition: all 0.3s ease;
}

.indicator-slide-enter-from,
.indicator-slide-leave-to {
    opacity: 0;
    transform: translateX(-20px);
}

.expand-enter-active,
.expand-leave-active {
    transition: all 0.2s ease;
}

.expand-enter-from,
.expand-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>
